import { useState, useEffect, useCallback, useRef } from 'react';
import permissionService from './permissionService';

/**
 * Hook personnalisé pour la gestion des permissions
 */
export const usePermissions = (companyName) => {
  const [permissions, setPermissions] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initRef = useRef(false);
  const timeoutRef = useRef(null);

  const initializePermissions = useCallback(async () => {
    // Éviter les initialisations multiples simultanées
    if (initRef.current) {
      console.log('🔄 Initialisation déjà en cours...');
      return;
    }

    try {
      initRef.current = true;
      setLoading(true);
      setError(null);
      
      const success = await permissionService.initializePermissions(companyName);
      
      if (success) {
        setPermissions(permissionService.permissions);
        setPlanInfo(permissionService.getPlanInfo());
      } else {
        setError('Erreur lors du chargement des permissions');
      }
    } catch (err) {
      setError('Erreur lors de l\'initialisation des permissions');
      console.error('Erreur hook permissions:', err);
    } finally {
      setLoading(false);
      initRef.current = false;
    }
  }, [companyName]);

  useEffect(() => {
    if (companyName && !initRef.current) {
      // Debounce pour éviter les appels rapprochés
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        initializePermissions();
      }, 50); // 50ms de debounce
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [companyName, initializePermissions]);

  const refreshPermissions = useCallback(async () => {
    if (companyName) {
      const success = await permissionService.refreshCache(companyName);
      if (success) {
        setPermissions(permissionService.permissions);
        setPlanInfo(permissionService.getPlanInfo());
      }
    }
  }, [companyName]);

  const hasFeature = useCallback((feature) => {
    return permissionService.hasFeature(feature);
  }, [permissions]);

  const getCurrentPlan = useCallback(() => {
    return permissionService.getCurrentPlan();
  }, [planInfo]);

  const getCurrentUsage = useCallback(() => {
    return permissionService.getCurrentUsage();
  }, [planInfo]);

  const getPlanLimits = useCallback(() => {
    return permissionService.getPlanLimits();
  }, [planInfo]);

  const isLimitExceeded = useCallback((limitType) => {
    return permissionService.isLimitExceeded(limitType);
  }, [planInfo]);

  const getUsagePercentage = useCallback((limitType) => {
    return permissionService.getUsagePercentage(limitType);
  }, [planInfo]);

  return {
    permissions,
    planInfo,
    loading,
    error,
    refreshPermissions,
    hasFeature,
    getCurrentPlan,
    getCurrentUsage,
    getPlanLimits,
    isLimitExceeded,
    getUsagePercentage
  };
};

/**
 * Hook pour vérifier une permission spécifique
 */
export const useFeaturePermission = (feature, companyName) => {
  const { permissions, loading, hasFeature } = usePermissions(companyName);
  
  return {
    hasPermission: hasFeature(feature),
    loading,
    permissions
  };
};

/**
 * Hook pour vérifier l'usage et les limites
 */
export const useUsageLimits = (companyName) => {
  const { planInfo, loading, getCurrentUsage, getPlanLimits, isLimitExceeded, getUsagePercentage } = usePermissions(companyName);
  
  return {
    usage: getCurrentUsage(),
    limits: getPlanLimits(),
    loading,
    planInfo,
    isLimitExceeded,
    getUsagePercentage
  };
}; 